/**
 * Unified workout data hook that abstracts CSV/API data sources
 * This replaces the WorkoutDataProvider pattern with a simpler direct hook approach
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { WorkoutService } from '../../services/workoutService'
import type { CategorizedOneRepMax, ExtendedCalendarDay, WeeklyStats, Workout } from '../../types'
import { queryKeys } from '../queryKeys'
import { useCSVWorkoutData } from './useCSVWorkoutData'
import { useUserCredentials } from './useCredentials'
import { useUserSettings } from './useSettings'

interface WorkoutDataResult {
  calendarData: ExtendedCalendarDay[]
  allExercises: CategorizedOneRepMax[]
  oneRepMaxes: CategorizedOneRepMax[]
  weeklyStats: WeeklyStats
  workouts: Workout[]
}

export interface UseWorkoutDataReturn {
  // Raw data
  workouts: Workout[]

  // Processed analytics data
  calendarData: ExtendedCalendarDay[]
  weeklyStats: WeeklyStats | null
  oneRepMaxes: CategorizedOneRepMax[]

  // State
  isLoading: boolean
  error: string | null
  isConfigured: boolean
  dataSource: 'api' | 'csv'

  // Actions
  refetch: () => void
}

/**
 * Main hook for accessing workout data from either CSV or API sources
 * Components should use this hook directly instead of context providers
 */
export function useWorkoutData(): UseWorkoutDataReturn {
  const { data: userSettings, isLoading: settingsLoading } = useUserSettings()
  const { data: userCredentials, isLoading: credentialsLoading } = useUserCredentials()
  const { data: csvWorkoutData, isLoading: csvLoading, error: csvError } = useCSVWorkoutData()

  // Determine data source and configuration
  const dataSource = userSettings?.settings?.dataSource || 'api'

  // Create stable identifiers for query key
  const apiKeyHash = userCredentials?.apiKey ? userCredentials.apiKey.slice(-8) : null

  // Check if data source is properly configured
  const hasValidApiConfig =
    dataSource === 'api' && userCredentials?.apiKey && userCredentials.apiKey.length > 10
  const hasValidCsvConfig =
    dataSource === 'csv' && csvWorkoutData?.workouts && csvWorkoutData.workouts.length > 0
  const isConfigured = hasValidApiConfig || hasValidCsvConfig

  // Add current local date to query key to ensure fresh data each day
  const currentDateKey = new Date().toLocaleDateString('sv-SE')
  
  const query = useQuery({
    queryKey: queryKeys.workouts.bySource(dataSource, apiKeyHash || '', currentDateKey),
    queryFn: async (): Promise<WorkoutDataResult> => {
      if (!userSettings || !userCredentials) {
        throw new Error('Settings not loaded')
      }

      const { apiKey } = userCredentials

      if (dataSource === 'api') {
        if (!apiKey) {
          throw new Error(
            'Hevy API key not configured. Please add your API key in Settings → Data Source.'
          )
        }
        return WorkoutService.fetchWorkoutData(dataSource, apiKey, undefined)
      }

      if (dataSource === 'csv') {
        if (csvError) {
          throw csvError
        }

        if (!csvWorkoutData) {
          throw new Error(
            'CSV data not uploaded. Please upload your Hevy export in Settings → Data Source.'
          )
        }

        try {
          return WorkoutService.fetchWorkoutDataFromCSV(csvWorkoutData.workouts || [])
        } catch (error) {
          console.error('Failed to process CSV workout data:', error)
          throw new Error(
            error instanceof Error
              ? `CSV processing failed: ${error.message}`
              : 'Failed to process CSV workout data'
          )
        }
      }

      throw new Error('Invalid data source configuration')
    },
    enabled: !settingsLoading && !credentialsLoading && !csvLoading && isConfigured,
    retry: false // Don't retry failed API calls
  })

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(() => {
    const data = query.data

    return {
      // Raw data
      workouts: data?.workouts || [],

      // Processed analytics data
      calendarData: data?.calendarData || [],
      weeklyStats: data?.weeklyStats || null,
      oneRepMaxes: data?.oneRepMaxes || [],

      // State
      isLoading: isConfigured ? query.isLoading : false,
      error: query.error ? query.error.message : null,
      isConfigured: isConfigured || false,
      dataSource,

      // Actions
      refetch: query.refetch
    }
  }, [query.data, query.isLoading, query.error, query.refetch, isConfigured, dataSource])
}

/**
 * Hook for paginated workout data (used by WorkoutsPage)
 * This provides a compatible interface for infinite scroll functionality
 */
export function useInfiniteWorkoutData(_options: { pageSize?: number } = {}) {
  const { workouts, isLoading, error } = useWorkoutData()

  return useMemo(() => {
    // For now, we return all workouts as a single page
    // True infinite scroll can be implemented later for API sources
    return {
      data: {
        pages: [{ workouts }],
        pageParams: [0]
      },
      fetchNextPage: () => Promise.resolve(),
      hasNextPage: false,
      isFetchingNextPage: false,
      isLoading,
      isError: !!error,
      error
    }
  }, [workouts, isLoading, error])
}
